import React from 'react';
import { Container, Typography, Box, Grid, Card, Avatar, useTheme, Chip } from '@mui/material';
import { Favorite, Security, VerifiedUser, People, Celebration, Star, TrendingUp, Business, Psychology, Handshake } from '@mui/icons-material';

const AboutUs = () => {
  const theme = useTheme();

  const stats = [
    { value: '2M+', label: 'Active Users', icon: <People />, color: '#FF6B6B' },
    { value: '100K+', label: 'Successful Matches', icon: <Favorite />, color: '#4ECDC4' },
    { value: '98%', label: 'Success Rate', icon: <Star />, color: '#45B7D1' },
    { value: '24/7', label: 'Support', icon: <VerifiedUser />, color: '#96CEB4' }
  ];

  const values = [
    {
      icon: <Security />,
      title: "Trust & Security",
      subtitle: "Your safety is our priority",
      description: "We implement advanced security measures and thorough profile verification to ensure a safe and trustworthy environment for all our users."
    },
    {
      icon: <VerifiedUser />,
      title: "Authenticity",
      subtitle: "Genuine connections only",
      description: "Every profile undergoes comprehensive verification to maintain the highest standards of authenticity and build lasting trust."
    },
    {
      icon: <Celebration />,
      title: "Celebration of Love",
      subtitle: "Every match matters",
      description: "We believe in the transformative power of love and celebrate every successful connection that leads to meaningful relationships."
    }
  ];

  const team = [
    {
      name: "Priya Sharma",
      role: "Founder & CEO",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
      description: "Passionate about connecting hearts and building meaningful relationships.",
      expertise: "Relationship Psychology"
    },
    {
      name: "Rahul Verma",
      role: "CTO",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
      description: "Leading our technology innovation to create seamless user experiences.",
      expertise: "AI & Machine Learning"
    },
    {
      name: "Anjali Patel",
      role: "Head of Operations",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
      description: "Ensuring smooth operations and exceptional customer service.",
      expertise: "Customer Experience"
    }
  ];

  return (
    <Box sx={{ bgcolor: '#fafafa', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box sx={{ 
        position: 'relative',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: 'white',
        overflow: 'hidden'
      }}>
        <Box
          component="img"
          src="https://images.unsplash.com/photo-1519741497674-611481863552?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2000&q=80"
          alt="Background"
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.15
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 15 }}>
          <Box sx={{ textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
            <Chip 
              label="Established 2020"
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                mb: 3,
                px: 3,
                py: 1,
                fontSize: '1rem',
                fontWeight: 600
              }}
            />
            <Typography 
              variant="h1" 
              sx={{ 
                fontWeight: 900,
                fontSize: { xs: '2.5rem', md: '4.5rem' },
                mb: 3,
                textShadow: '0 4px 8px rgba(0,0,0,0.3)',
                lineHeight: 1.1
              }}
            >
              About Us
            </Typography>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 300,
                mb: 4,
                opacity: 0.95,
                lineHeight: 1.4
              }}
            >
              Connecting hearts, building families, and creating love stories that last a lifetime
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Chip 
                icon={<Favorite />}
                label="Trusted by 2M+ Users"
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  fontSize: '1rem',
                  fontWeight: 600
                }}
              />
              <Chip 
                icon={<Star />}
                label="98% Success Rate"
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  fontSize: '1rem',
                  fontWeight: 600
                }}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Mission Section */}
      <Box sx={{ py: 15, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative' }}>
                <Typography 
                  variant="overline" 
                  sx={{ 
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    letterSpacing: 2,
                    mb: 2,
                    display: 'block'
                  }}
                >
                  OUR MISSION
                </Typography>
                <Typography 
                  variant="h2" 
                  sx={{ 
                    fontWeight: 800,
                    color: theme.palette.text.primary,
                    mb: 4,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    lineHeight: 1.2
                  }}
                >
                  Making Love Happen
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    lineHeight: 1.8,
                    mb: 4,
                    fontWeight: 400
                  }}
                >
                  We are dedicated to helping individuals find their perfect life partner through our advanced matching algorithm, 
                  comprehensive profile verification, and commitment to creating meaningful connections.
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    lineHeight: 1.8,
                    mb: 4
                  }}
                >
                  Founded with the vision of making the journey to finding love easier and more authentic, 
                  we understand the importance of cultural values, family traditions, and personal preferences 
                  in building lasting relationships.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip 
                    icon={<Psychology />}
                    label="Psychology-Based Matching"
                    sx={{ 
                      bgcolor: theme.palette.primary.light,
                      color: theme.palette.primary.main,
                      fontWeight: 600
                    }}
                  />
                  <Chip 
                    icon={<Business />}
                    label="Professional Approach"
                    sx={{ 
                      bgcolor: theme.palette.primary.light,
                      color: theme.palette.primary.main,
                      fontWeight: 600
                    }}
                  />
                </Box>
              </Box>
            </Grid>
           
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box sx={{ py: 15, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 10 }}>
            <Typography 
              variant="overline" 
              sx={{ 
                color: theme.palette.primary.main,
                fontWeight: 600,
                letterSpacing: 2,
                mb: 2,
                display: 'block'
              }}
            >
              OUR IMPACT
            </Typography>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 800,
                color: theme.palette.text.primary,
                mb: 3
              }}
            >
              Numbers That Matter
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: theme.palette.text.secondary,
                maxWidth: '600px',
                mx: 'auto',
                fontWeight: 400
              }}
            >
              Our commitment to excellence is reflected in the numbers that define our success
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Card sx={{ 
                  p: 4,
                  textAlign: 'center',
                  height: '220px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  transition: 'all 0.4s ease',
                  borderRadius: '20px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                  bgcolor: 'white',
                  '&:hover': {
                    transform: 'translateY(-12px)',
                    boxShadow: '0 25px 50px rgba(0,0,0,0.15)'
                  }
                }}>
                  <Box sx={{ 
                    color: stat.color,
                    mb: 3,
                    display: 'flex',
                    justifyContent: 'center'
                  }}>
                    {React.cloneElement(stat.icon, { sx: { fontSize: 50 } })}
                  </Box>
                  <Typography 
                    variant="h2" 
                    sx={{ 
                      color: theme.palette.text.primary,
                      fontWeight: 900,
                      mb: 1,
                      fontSize: { xs: '2.5rem', md: '3rem' }
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: theme.palette.text.secondary,
                      fontWeight: 600
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Values Section */}
      <Box sx={{ py: 15, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 10 }}>
            <Typography 
              variant="overline" 
              sx={{ 
                color: theme.palette.primary.main,
                fontWeight: 600,
                letterSpacing: 2,
                mb: 2,
                display: 'block'
              }}
            >
              OUR VALUES
            </Typography>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 800,
                color: theme.palette.text.primary,
                mb: 3
              }}
            >
              What Drives Us Forward
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: theme.palette.text.secondary,
                maxWidth: '600px',
                mx: 'auto',
                fontWeight: 400
              }}
            >
              Our core values shape every decision we make and every feature we build
            </Typography>
          </Box>
          <Grid container spacing={4} wrap="nowrap" sx={{ overflowX: 'auto', pb: 2 }}>
            {values.map((value, index) => (
              <Grid item xs={12} md={4} key={index} sx={{ minWidth: 340, maxWidth: 340 }}>
                <Card sx={{ 
                  p: 5,
                  textAlign: 'center',
                  height: '380px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  transition: 'all 0.4s ease',
                  borderRadius: '24px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                  bgcolor: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '6px',
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    zIndex: 1
                  },
                  '&:hover': {
                    transform: 'translateY(-15px)',
                    boxShadow: '0 30px 60px rgba(0,0,0,0.15)'
                  }
                }}>
                  <Box sx={{ 
                    color: theme.palette.primary.main,
                    mb: 4,
                    display: 'flex',
                    justifyContent: 'center'
                  }}>
                    {React.cloneElement(value.icon, { sx: { fontSize: 70 } })}
                  </Box>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                      mb: 1
                    }}
                  >
                    {value.title}
                  </Typography>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      color: theme.palette.primary.main,
                      mb: 3,
                      fontWeight: 600
                    }}
                  >
                    {value.subtitle}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: theme.palette.text.secondary,
                      lineHeight: 1.7
                    }}
                  >
                    {value.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Team Section */}
      <Box sx={{ py: 15, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 10 }}>
            <Typography 
              variant="overline" 
              sx={{ 
                color: theme.palette.primary.main,
                fontWeight: 600,
                letterSpacing: 2,
                mb: 2,
                display: 'block'
              }}
            >
              OUR TEAM
            </Typography>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 800,
                color: theme.palette.text.primary,
                mb: 3
              }}
            >
              Meet the Minds Behind the Magic
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: theme.palette.text.secondary,
                maxWidth: '600px',
                mx: 'auto',
                fontWeight: 400
              }}
            >
              Our passionate team is dedicated to making your journey to love successful and meaningful
            </Typography>
          </Box>
          <Grid container spacing={4} wrap="nowrap" sx={{ overflowX: 'auto', pb: 2 }}>
            {team.map((member, index) => (
              <Grid item xs={12} md={4} key={index} sx={{ minWidth: 340, maxWidth: 340 }}>
                <Card sx={{ 
                  p: 5,
                  textAlign: 'center',
                  height: '450px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  transition: 'all 0.4s ease',
                  borderRadius: '24px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                  bgcolor: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '6px',
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    zIndex: 1
                  },
                  '&:hover': {
                    transform: 'translateY(-15px)',
                    boxShadow: '0 30px 60px rgba(0,0,0,0.15)'
                  }
                }}>
                  <Avatar 
                    src={member.image}
                    sx={{ 
                      width: 140,
                      height: 140,
                      mx: 'auto',
                      mb: 4,
                      border: `6px solid ${theme.palette.primary.main}`,
                      boxShadow: '0 15px 35px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                      mb: 1
                    }}
                  >
                    {member.name}
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: theme.palette.primary.main,
                      mb: 2,
                      fontWeight: 600
                    }}
                  >
                    {member.role}
                  </Typography>
                  <Chip 
                    label={member.expertise}
                    sx={{ 
                      bgcolor: theme.palette.primary.light,
                      color: theme.palette.primary.main,
                      mb: 3,
                      fontWeight: 600
                    }}
                  />
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: theme.palette.text.secondary,
                      lineHeight: 1.7
                    }}
                  >
                    {member.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Story Section */}
      <Box sx={{ py: 15, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
           
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative' }}>
                <Typography 
                  variant="overline" 
                  sx={{ 
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    letterSpacing: 2,
                    mb: 2,
                    display: 'block'
                  }}
                >
                  OUR STORY
                </Typography>
                <Typography 
                  variant="h2" 
                  sx={{ 
                    fontWeight: 800,
                    color: theme.palette.text.primary,
                    mb: 4,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    lineHeight: 1.2
                  }}
                >
                  From Dream to Reality
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    lineHeight: 1.8,
                    mb: 4,
                    fontWeight: 400
                  }}
                >
                  Started in 2020, our platform was born from a simple belief: everyone deserves to find their perfect match. 
                  What began as a small team with a big dream has grown into a trusted community of millions.
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    lineHeight: 1.8,
                    mb: 4
                  }}
                >
                  Today, we continue to innovate and improve our matching algorithms, enhance security measures, 
                  and create features that make the journey to finding love more meaningful and successful.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip 
                    icon={<Handshake />}
                    label="Trusted Platform"
                    sx={{ 
                      bgcolor: theme.palette.primary.light,
                      color: theme.palette.primary.main,
                      fontWeight: 600
                    }}
                  />
                  <Chip 
                    icon={<TrendingUp />}
                    label="Growing Success"
                    sx={{ 
                      bgcolor: theme.palette.primary.light,
                      color: theme.palette.primary.main,
                      fontWeight: 600
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default AboutUs; 